/** Protocollo condiviso con app ZION — mai mostrato come messaggio chat. */
import { bytesToBase64, base64ToBytes } from '../crypto/base64.ts'

export const TEMP_CHAT_CTRL_PREFIX = 'ZION::TEMP_CHAT::'

export type TempChatWireAction = 'REQ' | 'ACK' | 'REJ' | 'CANCEL' | 'MSG'

export interface TempChatWirePayload {
  action: TempChatWireAction
  /** Lane temp:xxxxxxxxxxxx */
  lane: string
  code?: string
  expiresAt?: number
  message?: string
  messageId?: string
}

export function generateTempLane(): string {
  const id = crypto.randomUUID().replace(/-/g, '').slice(0, 12).toLowerCase()
  return `temp:${id}`
}

export function generateTempInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let code = 'TC-'
  for (let i = 0; i < 5; i++) {
    code += chars[Math.floor(Math.random() * chars.length)]!
  }
  return code
}

export function buildTempChatReq(lane: string, code: string, expiresAt: number): string {
  return `${TEMP_CHAT_CTRL_PREFIX}REQ|${lane.trim().toLowerCase()}|${code}|${expiresAt}`
}

export function buildTempChatAck(lane: string): string {
  return `${TEMP_CHAT_CTRL_PREFIX}ACK|${lane.trim().toLowerCase()}`
}

export function buildTempChatRej(lane: string): string {
  return `${TEMP_CHAT_CTRL_PREFIX}REJ|${lane.trim().toLowerCase()}`
}

export function buildTempChatCancel(lane: string): string {
  return `${TEMP_CHAT_CTRL_PREFIX}CANCEL|${lane.trim().toLowerCase()}`
}

function encodeTempMsgPayload(text: string): string {
  return bytesToBase64(new TextEncoder().encode(text))
}

function decodeTempMsgPayload(b64: string): string {
  return new TextDecoder().decode(base64ToBytes(b64))
}

export function buildTempChatMsg(lane: string, messageId: string, plainText: string): string {
  return `${TEMP_CHAT_CTRL_PREFIX}MSG|${lane.trim().toLowerCase()}|${messageId.trim()}|${encodeTempMsgPayload(plainText)}`
}

export function parseTempChatControl(text: string): TempChatWirePayload | null {
  const t = text.trim().replace(/\s*::\s*/g, '::').replace(/\s*\|\s*/g, '|').replace(/temp\s*:\s*/gi, 'temp:')
  if (!t.startsWith(TEMP_CHAT_CTRL_PREFIX)) return null

  const rest = t.slice(TEMP_CHAT_CTRL_PREFIX.length).trim()
  const parts = rest.split('|').map((p) => p.trim())
  const action = (parts[0] ?? '').toUpperCase() as TempChatWireAction
  if (!action) return null

  if (action === 'REQ') {
    const lane = parts[1] ?? ''
    const code = parts[2] ?? ''
    const expiresAt = Number(parts[3]) || undefined
    if (!lane.trim()) return null
    return { action, lane: lane.trim().toLowerCase(), code, expiresAt }
  }

  if (action === 'ACK' || action === 'REJ' || action === 'CANCEL') {
    const lane = parts[1] ?? ''
    if (!lane.trim()) return null
    return { action, lane: lane.trim().toLowerCase() }
  }

  if (action === 'MSG') {
    const lane = parts[1] ?? ''
    const messageId = parts[2] ?? ''
    const payloadB64 = parts[3] ?? ''
    if (!lane.trim() || !messageId || !payloadB64) return null
    try {
      const message = decodeTempMsgPayload(payloadB64)
      if (!message.trim()) return null
      return { action, lane: lane.trim().toLowerCase(), messageId, message }
    } catch {
      return null
    }
  }

  return null
}

export function isTempChatControlPlain(text: string): boolean {
  return parseTempChatControl(text) !== null
}
